import { useEffect, useState } from 'react';
import { Link as RouterLink, useHistory, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import dayjs from 'dayjs';

import { useConfirm } from 'material-ui-confirm';
import { Avatar, Button, Typography } from '@mui/material';
import Grid from 'material/components/Grid';
import { DeleteOutline as DeleteOutlineIcon, Edit as EditIcon } from '@mui/icons-material';

import { deleteMeeting, fetchMeeting } from 'store/actions/meetings';
import { meetingsSelectors } from 'store/selectors/meetings';
import { setSnackbarSuccess } from 'store/actions/global';

import { Backdrop, BreadcrumbsNavigation, Container, LinkText } from 'common/components';
import TopCard from 'common/containers/TopCard';

import { CardWrapper } from 'globalStyles';
import { colors } from 'themes';

const avatarColors = [colors.headerBackground, colors.primary, colors.midnightBlue];

const Eyebrow = styled(Typography)`
  margin-bottom: 14px;
  color: ${colors.osloGray};
  font-weight: 600;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 1.2px;
`;

const OfficialRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 14px 4px;

  &:not(:first-of-type) {
    border-top: 1px solid ${colors.borderColor2};
  }
`;

const OfficialName = styled(Typography)`
  font-weight: 600;
`;

const OfficialOffice = styled(Typography)`
  color: ${colors.osloGray};
  font-size: 13.5px;
`;

const NotesBody = styled.div`
  max-width: 65ch;

  p {
    margin: 0 0 14px;
    font-size: 15px;
    line-height: 1.65;
  }

  p:last-child {
    margin-bottom: 0;
  }
`;

const EmptyMessage = styled(Typography)`
  color: ${colors.osloGray};
`;

const officialInitials = name => {
  const words = (name || '').trim().split(/\s+/);
  const first = words[0]?.[0] || '';
  const last = words.length > 1 ? words[words.length - 1][0] : '';

  return `${first}${last}`.toUpperCase();
};

const MeetingDetails = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const confirm = useConfirm();

  const { meetingUuid, organizationUuid } = useParams();

  const { isFetching } = useSelector(state => ({
    isFetching: meetingsSelectors.isFetching(state),
  }));

  const [meeting, setMeeting] = useState(null);

  useEffect(() => {
    dispatch(fetchMeeting(meetingUuid)).then(data => setMeeting(data));
  }, [meetingUuid]);

  const breadcrumbs = [
    {
      link: `/${organizationUuid}/contacts/lists`,
      name: 'Lists',
    },
    {
      link: `/${organizationUuid}/meetings`,
      name: 'Meetings',
    },
    {
      link: '',
      name: meeting?.title || 'Meeting',
    },
  ];

  const handleRemoveMeeting = () => {
    confirm({
      confirmationText: 'Remove',
      title: 'Remove Meeting?',
    })
      .then(() => dispatch(deleteMeeting(meetingUuid)))
      .then(() => dispatch(setSnackbarSuccess()))
      .then(() => history.push(`/${organizationUuid}/meetings`));
  };

  const targets = meeting?.targets || [];
  const contacts = meeting?.contacts || [];
  const notesParagraphs = (meeting?.description || '')
    .split(/\n+/)
    .filter(paragraph => paragraph.trim());

  return (
    <>
      <BreadcrumbsNavigation isLoading={isFetching} links={breadcrumbs} />
      <Container marginBottom="40">
        <Grid container>
          <TopCard
            backButtonLink={`/${organizationUuid}/meetings`}
            dateInfo={
              meeting?.occurredOn
                ? `Met on ${dayjs(meeting.occurredOn).format('MMMM D, YYYY')}`
                : ''
            }
            restActions={
              <>
                <Button
                  color="error"
                  onClick={handleRemoveMeeting}
                  startIcon={<DeleteOutlineIcon />}
                >
                  Delete
                </Button>
                <Button
                  color="primary"
                  component={RouterLink}
                  disableElevation
                  startIcon={<EditIcon />}
                  to={`/${organizationUuid}/meetings/${meetingUuid}/edit`}
                  variant="contained"
                >
                  Edit Meeting
                </Button>
              </>
            }
            title={meeting?.title || ''}
          />
        </Grid>
        <CardWrapper>
          <Eyebrow>{`Contacts${contacts.length ? ` · ${contacts.length} attended` : ''}`}</Eyebrow>
          {contacts.length ? (
            contacts.map((contact, index) => {
              const contactName = `${contact.firstName || ''} ${contact.lastName || ''}`.trim();

              return (
                <OfficialRow key={contact.contact}>
                  <Avatar sx={{ backgroundColor: avatarColors[index % avatarColors.length] }}>
                    {officialInitials(contactName)}
                  </Avatar>
                  <OfficialName variant="body1">{contactName}</OfficialName>
                </OfficialRow>
              );
            })
          ) : (
            <EmptyMessage variant="body2">No contacts recorded</EmptyMessage>
          )}
        </CardWrapper>
        <CardWrapper>
          <Eyebrow>{`Officials${targets.length ? ` · ${targets.length} attended` : ''}`}</Eyebrow>
          {targets.length ? (
            targets.map((target, index) => (
              <OfficialRow key={target.term || target.customTarget || target.officialName}>
                <Avatar sx={{ backgroundColor: avatarColors[index % avatarColors.length] }}>
                  {officialInitials(target.officialName)}
                </Avatar>
                <div>
                  <OfficialName variant="body1">{target.officialName}</OfficialName>
                  <OfficialOffice variant="body2">
                    {[target.office, target.state].filter(Boolean).join(' · ')}
                  </OfficialOffice>
                </div>
              </OfficialRow>
            ))
          ) : (
            <EmptyMessage variant="body2">No officials recorded</EmptyMessage>
          )}
        </CardWrapper>
        <CardWrapper>
          <Eyebrow>Notes</Eyebrow>
          {notesParagraphs.length ? (
            <NotesBody>
              {notesParagraphs.map(paragraph => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </NotesBody>
          ) : (
            <EmptyMessage variant="body2">
              {'No notes recorded — '}
              <LinkText to={`/${organizationUuid}/meetings/${meetingUuid}/edit`}>
                add notes
              </LinkText>
            </EmptyMessage>
          )}
        </CardWrapper>
      </Container>
      <Backdrop isOpen={isFetching} />
    </>
  );
};

export default MeetingDetails;
